import React from "react";
import * as Yup from "yup";
import { EDIT_PATIENT_PROFILE_DETAILS } from "./constants";
import { Form, Formik } from "formik";
import Button from "../../Molecules/button/Button";
import Input from "../../Atoms/Input/Input";
import DatePicker from "../../Atoms/DatePicker/DatePicker";
import ContactInput from "../../Atoms/Input/ContactInput";
import moment from "moment";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import UnitNumberSelect from "../../Molecules/unit-number-select/UnitNumberSelect";

interface EditProfileProps {
  defaultSelectedData?: any;
  handleOnSubmittedData?: (formData: any) => void;
  handleClose?: () => void;
}

const EditProfile = ({
  defaultSelectedData,
  handleOnSubmittedData,
  handleClose,
}: EditProfileProps) => {
  const validationSchema = Yup.object().shape({
    [EDIT_PATIENT_PROFILE_DETAILS.FIRST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "First name can only contain characters"
      )
      .required("First name is required"),
    [EDIT_PATIENT_PROFILE_DETAILS.LAST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "Last name can only contain characters"
      )
      .required("Last name is required"),
    [EDIT_PATIENT_PROFILE_DETAILS.EMAIL]: Yup.string()
      // .email("Enter a valid email address")
      .matches(VALIDATION_CONSTANTS.EMAIL, "Enter a valid email address")
      .required("Email is required"),
    [EDIT_PATIENT_PROFILE_DETAILS.CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("Contact number is required"),
    [EDIT_PATIENT_PROFILE_DETAILS.BSA_ID]: Yup.number()
      // .matches(VALIDATION_CONSTANTS.NUMERIC, "BSA ID must contain only numbers")
      .required("BSA ID is required"),
    [EDIT_PATIENT_PROFILE_DETAILS.DATE_OF_BIRTH]: Yup.date()
      .max(new Date(), "Date of birth cannot be a future date")
      .required("Date Of birth is required"),
    // Unit fields are auto-filled from the selected Unit Number and are no
    // longer user-entered / mandatory.
  });

  const commonGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px 16px",
  };

  return (
    <Formik
      initialValues={{
        [EDIT_PATIENT_PROFILE_DETAILS.FIRST_NAME]: `${defaultSelectedData?.firstName ? defaultSelectedData?.firstName : ""}`,
        [EDIT_PATIENT_PROFILE_DETAILS.LAST_NAME]: `${defaultSelectedData?.lastName ? defaultSelectedData?.lastName : ""}`,
        [EDIT_PATIENT_PROFILE_DETAILS.EMAIL]: `${defaultSelectedData?.email ? defaultSelectedData?.email : ""}`,
        [EDIT_PATIENT_PROFILE_DETAILS.CONTACT_NUMBER]: `${defaultSelectedData?.contactNumber ? defaultSelectedData?.contactNumber : ""}`,
        [EDIT_PATIENT_PROFILE_DETAILS.BSA_ID]: `${defaultSelectedData?.bsaId ? defaultSelectedData?.bsaId : ""}`,
        [EDIT_PATIENT_PROFILE_DETAILS.DATE_OF_BIRTH]:
          defaultSelectedData?.dateOfBirth
            ? // DOB is a date-only value stored as midnight UTC — read it in UTC
              // so the picker prefills the correct calendar date (local time
              // would roll US users back a day).
              `${moment.utc(defaultSelectedData?.dateOfBirth)?.format("MM/DD/YYYY")}`
            : undefined,
        [EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_NUMBER]: `${defaultSelectedData?.baseCamp?.unitNumber ? defaultSelectedData?.baseCamp?.unitNumber : ""}`,
        [EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_NAME]: `${defaultSelectedData?.baseCamp?.leaderName ? defaultSelectedData?.baseCamp?.leaderName : ""}`,
        [EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_EMAIL]: `${defaultSelectedData?.baseCamp?.email ? defaultSelectedData?.baseCamp?.email : ""}`,
        [EDIT_PATIENT_PROFILE_DETAILS.CLINIC_LOCATION]: `${defaultSelectedData?.baseCamp?.id ? defaultSelectedData?.baseCamp?.id : ""}`,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        try {
          handleOnSubmittedData && handleOnSubmittedData(values);
          // Handle form data here
          // If needed, call an API or further process the values here
        } catch (error) {
          console.log(error);
        } finally {
          setSubmitting(false); // Reset submitting state
        }
      }}
    >
      {({
        errors,
        submitForm,
        isSubmitting,
        handleReset,
        isValid,
        values,
        touched,
        setFieldValue,
        handleBlur,
        handleSubmit,
      }) => {
        return (
          <Form
            onSubmit={handleSubmit}
            className="h-full flex flex-col justify-between rounded-lg"
          >
            <div className="w-full flex flex-col rounded-lg">
              {/* PATIENT DETAILS */}
              <div>
                {/* <Heading title="Patient Details" customStyle={headerStyle} /> */}
                <div style={commonGridStyle}>
                  <Input
                    // disabled
                    textFieldLabel="First Name"
                    isStarPresent={true}
                    name={EDIT_PATIENT_PROFILE_DETAILS.FIRST_NAME}
                    value={values[EDIT_PATIENT_PROFILE_DETAILS.FIRST_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.FIRST_NAME,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.FIRST_NAME] &&
                      Boolean(errors[EDIT_PATIENT_PROFILE_DETAILS.FIRST_NAME])
                    }
                    helperText={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.FIRST_NAME]
                        ? errors[EDIT_PATIENT_PROFILE_DETAILS.FIRST_NAME]
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="Last Name"
                    isStarPresent={true}
                    name={EDIT_PATIENT_PROFILE_DETAILS.LAST_NAME}
                    value={values[EDIT_PATIENT_PROFILE_DETAILS.LAST_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.LAST_NAME,
                        e.target.value
                      );
                    }}
                    size="small"
                    onBlur={handleBlur}
                    error={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.LAST_NAME] &&
                      Boolean(errors[EDIT_PATIENT_PROFILE_DETAILS.LAST_NAME])
                    }
                    helperText={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.LAST_NAME]
                        ? errors[EDIT_PATIENT_PROFILE_DETAILS.LAST_NAME]
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="Email"
                    isStarPresent={true}
                    name={EDIT_PATIENT_PROFILE_DETAILS.EMAIL}
                    value={values[EDIT_PATIENT_PROFILE_DETAILS.EMAIL]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.EMAIL,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.EMAIL] &&
                      Boolean(errors[EDIT_PATIENT_PROFILE_DETAILS.EMAIL])
                    }
                    helperText={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.EMAIL]
                        ? errors[EDIT_PATIENT_PROFILE_DETAILS.EMAIL]
                        : undefined
                    }
                  />
                  <ContactInput
                    // disabled
                    textFieldLabel="Contact Number"
                    isStarPresent={true}
                    name={EDIT_PATIENT_PROFILE_DETAILS.CONTACT_NUMBER}
                    value={values[EDIT_PATIENT_PROFILE_DETAILS.CONTACT_NUMBER]}
                    onChangeCb={(value) => {
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.CONTACT_NUMBER,
                        value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.CONTACT_NUMBER] &&
                      Boolean(
                        errors[EDIT_PATIENT_PROFILE_DETAILS.CONTACT_NUMBER]
                      )
                    }
                    helperText={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.CONTACT_NUMBER]
                        ? errors[EDIT_PATIENT_PROFILE_DETAILS.CONTACT_NUMBER]
                        : undefined
                    }
                    isContactNumber={true}
                  />

                  <Input
                    // disabled
                    type="number"
                    textFieldLabel="BSA ID"
                    isStarPresent={true}
                    name={EDIT_PATIENT_PROFILE_DETAILS.BSA_ID}
                    value={values[EDIT_PATIENT_PROFILE_DETAILS.BSA_ID]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.BSA_ID,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.BSA_ID] &&
                      Boolean(errors[EDIT_PATIENT_PROFILE_DETAILS.BSA_ID])
                    }
                    helperText={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.BSA_ID]
                        ? errors[EDIT_PATIENT_PROFILE_DETAILS.BSA_ID]
                        : undefined
                    }
                  />

                  <DatePicker
                    name={EDIT_PATIENT_PROFILE_DETAILS.DATE_OF_BIRTH}
                    value={values[EDIT_PATIENT_PROFILE_DETAILS.DATE_OF_BIRTH]}
                    onChangeCb={(value) => {
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.DATE_OF_BIRTH,
                        value
                      );
                    }}
                    isStarPresent={true}
                    label="Date Of Birth"
                    style={{ width: "100%" }}
                    maxDate={moment()}
                    error={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.DATE_OF_BIRTH] &&
                      Boolean(
                        errors[EDIT_PATIENT_PROFILE_DETAILS.DATE_OF_BIRTH]
                      )
                    }
                    helperText={
                      touched[EDIT_PATIENT_PROFILE_DETAILS.DATE_OF_BIRTH]
                        ? errors[EDIT_PATIENT_PROFILE_DETAILS.DATE_OF_BIRTH]
                        : undefined
                    }
                  />

                  <UnitNumberSelect
                    label="Unit Number"
                    allowCreate
                    defaultValue={
                      defaultSelectedData?.baseCamp?.id
                        ? {
                            label: defaultSelectedData?.baseCamp?.unitNumber,
                            value: defaultSelectedData?.baseCamp?.id,
                          }
                        : null
                    }
                    onChange={(record) => {
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.CLINIC_LOCATION,
                        record?.id || ""
                      );
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_NUMBER,
                        record?.unitNumber || ""
                      );
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_NAME,
                        record?.leaderName || ""
                      );
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_EMAIL,
                        record?.email || ""
                      );
                    }}
                  />

                  <Input
                    disabled
                    textFieldLabel="Jamboree Unit Leader Name"
                    name={
                      EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_NAME
                    }
                    value={
                      values[
                        EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_NAME
                      ]
                    }
                    onChangeCb={(e) => {
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_NAME,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[
                        EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_NAME
                      ] &&
                      Boolean(
                        errors[
                          EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_NAME
                        ]
                      )
                    }
                    helperText={
                      touched[
                        EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_NAME
                      ]
                        ? errors[
                            EDIT_PATIENT_PROFILE_DETAILS
                              .JAMBOREE_UNIT_LEADER_NAME
                          ]
                        : undefined
                    }
                  />

                  <Input
                    disabled
                    textFieldLabel="Jamboree Unit Leader Email"
                    name={
                      EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_EMAIL
                    }
                    value={
                      values[
                        EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_EMAIL
                      ]
                    }
                    onChangeCb={(e) => {
                      setFieldValue(
                        EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_EMAIL,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[
                        EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_EMAIL
                      ] &&
                      Boolean(
                        errors[
                          EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_EMAIL
                        ]
                      )
                    }
                    helperText={
                      touched[
                        EDIT_PATIENT_PROFILE_DETAILS.JAMBOREE_UNIT_LEADER_EMAIL
                      ]
                        ? errors[
                            EDIT_PATIENT_PROFILE_DETAILS
                              .JAMBOREE_UNIT_LEADER_EMAIL
                          ]
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-10 gap-4">
              <Button
                label={TEXT_CONSTANTS?.CANCEL}
                variant="outlined"
                onClickCb={() => {
                  handleClose?.();
                }}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  backgroundColor: "white",
                  border: "none",
                  color: "#393939",
                }}
              />
              <Button
                // disabled={!isValid} // Button disabled if form is invalid or submitting
                type="submit"
                label={TEXT_CONSTANTS?.SAVE}
                variant="contained"
                onClickCb={() => {}}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                }}
              ></Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EditProfile;
